'use client';

import { useState, useEffect } from 'react';
import { Button, Input, Select, SelectItem, Textarea } from '@heroui/react';
import useClientStore from '@/store/useClientStore';
import useCustomerStore from '@/store/useCustomerStore';
import { supabase } from '@/lib/supabase';
import { Mail, Dealership, LeadTypes } from '@/utils/types';
import OpeningHours from '@/sections/home/OpeningHours';
import { getDealershipHours, type PublicDayHours } from '@/lib/dealership-hours';
import { sucursalesDelCliente, horasDelCliente } from '@/lib/sucursales-del-store';
import { DEALERSHIP_PUBLIC_COLS } from '@/lib/website-config-cols';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { isBrasil, forcedSiteLanguage } from '@/lib/country';
import { sendSiteFormEmail, idNumerico } from '@/lib/site-form-email';
import SuccessModal from '@/components/ui/SuccessModal';

/**
 * Motivos del formulario. La KEY es el VALOR que se guarda/compara (mails.reason
 * vía mapReasonToType, cuerpo del correo en Chile): queda en español siempre.
 * Sólo la etiqueta visible se traduce.
 */
const MOTIVOS = [
  { key: 'Compra de Vehículo', label: 'formsUi.contact.reasons.buy' },
  { key: 'Venta de Vehículo', label: 'formsUi.contact.reasons.sell' },
  { key: 'Consulta General', label: 'formsUi.contact.reasons.general' },
  { key: 'Otro', label: 'formsUi.contact.reasons.other' },
] as const;

const FORMULARIO_VACIO = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  reason: '',
  message: '',
};

interface FormStyleProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
  embedded?: boolean;
}

const ContactForm = ({ title, subtitle, bgColor, textColor, accentColor, embedded = false }: FormStyleProps = {}) => {
  // When builder passes bgColor, use inline styles. Otherwise use default Tailwind classes.
  const hasBuilderStyles = !!bgColor;
  const isDarkBg = bgColor && (bgColor.startsWith('#0') || bgColor.startsWith('#1') || bgColor.startsWith('#2'));
  const cardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const cardClass = hasBuilderStyles
    ? 'rounded-xl shadow-lg p-8 border'
    : 'bg-white dark:bg-dark-card rounded-xl shadow-lg p-8 border border-gray-200 dark:border-dark-border';
  const infoCardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor ? `${bgColor}f0` : undefined, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const infoCardClass = hasBuilderStyles
    ? 'rounded-xl p-8 border'
    : 'bg-gray-50 dark:bg-dark-card rounded-xl p-8 border border-gray-200 dark:border-dark-border';
  const titleStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const titleClass = hasBuilderStyles ? 'text-3xl sm:text-4xl font-extrabold' : 'text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white';
  const subtitleStyle = hasBuilderStyles ? { color: textColor, opacity: 0.6 } : undefined;
  const subtitleClass = hasBuilderStyles ? 'mt-4 text-lg' : 'mt-4 text-lg text-gray-500 dark:text-gray-400';
  const headingStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const headingClass = hasBuilderStyles ? 'text-2xl font-bold' : 'text-2xl font-bold text-gray-900 dark:text-white';
  const subHeadingClass = hasBuilderStyles ? 'text-lg font-medium' : 'text-lg font-medium text-gray-900 dark:text-white';
  const bodyStyle = hasBuilderStyles ? { color: textColor, opacity: 0.7 } : undefined;
  const bodyClass = hasBuilderStyles ? '' : 'text-gray-600 dark:text-gray-400';

  // HeroUI input classNames to match builder theme
  const inputClassNames = (hasBuilderStyles || embedded) ? {
    label: isDarkBg ? '!text-white/60' : '!text-black/50',
    input: isDarkBg ? '!text-white !placeholder-white/40' : '!text-gray-900',
    inputWrapper: isDarkBg
      ? '!bg-[#262626] !border-[#3a3a3a] hover:!border-[#4a4a4a] !rounded-lg'
      : '!bg-white !border-[#d1d5db] hover:!border-gray-400 !rounded-lg',
  } : undefined;
  const buttonStyle = (hasBuilderStyles || embedded) && accentColor ? { backgroundColor: accentColor } : undefined;

  const { client } = useClientStore();
  const { initializeCustomer } = useCustomerStore();
  const { t } = useTranslation();
  // El correo y el registro en `mails` los lee la automotora: en Brasil, en
  // portugués. La rama de Chile (y sin país) es el texto de siempre.
  const esBrasil = isBrasil(client?.country);
  // EE.UU.: lo mismo, en inglés.
  const enUsa = forcedSiteLanguage(client?.country) === 'en';
  const [loading, setLoading] = useState(false);
  const [dealership, setDealership] = useState<Dealership | null>(null);
  // Ticket #59: el horario sale de Agenda (dealership_hours) por RPC, no del
  // JSONB legacy `dealerships.opening_hours`.
  const [hours, setHours] = useState<PublicDayHours[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState(FORMULARIO_VACIO);

  // Trae la sucursal para mostrar dirección/teléfono/email/horario reales desde
  // la plataforma (dealerships), igual que el componente "¿Cómo llegar?".
  useEffect(() => {
    const fetchDealership = async () => {
      if (!client?.id) return;
      // Sucursales y horario: del store si ya vienen con el tenant (los
      // resuelve el layout); si no, de la base como antes.
      let data = sucursalesDelCliente<Dealership>(client);
      if (!data) {
        ({ data } = await supabase
          .from('dealerships')
          .select<string, Dealership>(DEALERSHIP_PUBLIC_COLS)
          .eq('client_id', client.id));
      }
      if (data && data.length > 0) {
        const principal = data[0] as Dealership;
        setDealership(principal);
        // El horario es de ESA sucursal, así que se pide recién acá, con su id.
        const enStore = horasDelCliente(client);
        const porSucursal = enStore ?? (await getDealershipHours(client.id, Number(principal.id)));
        setHours(porSucursal[Number(principal.id)] || []);
      }
    };
    fetchDealership();
  }, [client?.id]);

  // Datos de contacto: prioriza la sucursal, con fallback al contacto del cliente.
  const contactAddress = dealership?.address || client?.contact?.address;
  // Ticket #58: la sucursal puede tener varios correos (`emails`), con
  // fallback al `email` único y, si no hay sucursal, al contacto del tenant.
  const dealershipEmailList = (
    Array.isArray((dealership as { emails?: string[] | null } | null)?.emails) &&
    (dealership as { emails?: string[] | null }).emails!.length
      ? (dealership as { emails?: string[] | null }).emails!
      : dealership?.email
        ? [dealership.email]
        : []
  )
    .map((e) => String(e).trim())
    .filter(Boolean);
  const contactEmails = dealershipEmailList.length
    ? dealershipEmailList
    : client?.contact?.email
      ? [client.contact.email]
      : [];
  const contactPhone = dealership?.phone || client?.contact?.phone;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      // Primero inicializamos o actualizamos el customer
      const customerResponse = await initializeCustomer({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        client_id: client?.id || '',
      });

      // Brasil: la etiqueta del motivo en portugués (el valor guardado en
      // `reason` sigue siendo el de siempre, vía mapReasonToType).
      const motivoBr = esBrasil || enUsa
        ? (() => {
            const m = MOTIVOS.find((x) => x.key === formData.reason);
            return m ? t(m.label) : formData.reason;
          })()
        : formData.reason;

      // Guardamos el email en la base de datos
      const { error: mailError } = await supabase.from('mails').insert([
        {
          customer_id: customerResponse.id, // Usamos el ID del customer
          subject: esBrasil
            ? `Novo contato de ${client?.name}`
            : enUsa
              ? `New contact from ${client?.name}`
              : `Nuevo contacto desde ${client?.name}`,
          body: enUsa
            ? `
Name: ${formData.first_name} ${formData.last_name}
Email: ${formData.email}
Phone: ${formData.phone}
Reason: ${motivoBr}

Message:
${formData.message}
        `
            : esBrasil
            ? `
Nome: ${formData.first_name} ${formData.last_name}
E-mail: ${formData.email}
Telefone: ${formData.phone}
Motivo: ${motivoBr}

Mensagem:
${formData.message}
        `
            : `
Nombre: ${formData.first_name} ${formData.last_name}
Email: ${formData.email}
Teléfono: ${formData.phone}
Motivo: ${formData.reason}

Mensaje:
${formData.message}
        `,
          reason: mapReasonToType(formData.reason),
        },
      ]);

      if (mailError) throw mailError;

      // Aviso a la automotora: la edge resuelve destinatario, asunto y HTML.
      const aviso = await sendSiteFormEmail(
        {
          client_id: idNumerico(client?.id) ?? 0,
          kind: 'contact',
          vehicle_id: null,
          params: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone,
            reason: motivoBr,
            message: formData.message,
          },
        },
        null
      );

      if (aviso.success && !aviso.skipped) {
        setFormData(FORMULARIO_VACIO);
        setShowSuccessModal(true);
        return;
      }

      // Sin aviso y sin dirección no hay cómo llegar a la automotora: se muestra el error.
      if (!client?.contact?.email) throw new Error('Automotora sin correo de contacto');

      // Respaldo si el aviso no salió: el correo del visitante, como antes.
      // Crear la URL del mailto con los parámetros codificados
      const emailBody = enUsa
        ? `
New contact message:

Name: ${formData.first_name} ${formData.last_name}
Email: ${formData.email}
Phone: ${formData.phone}
Reason: ${motivoBr}

Message:
${formData.message}
    `
        : esBrasil
        ? `
Nova mensagem de contato:

Nome: ${formData.first_name} ${formData.last_name}
E-mail: ${formData.email}
Telefone: ${formData.phone}
Motivo: ${motivoBr}

Mensagem:
${formData.message}
    `
        : `
Nuevo mensaje de contacto:

Nombre: ${formData.first_name} ${formData.last_name}
Email: ${formData.email}
Teléfono: ${formData.phone}
Motivo: ${formData.reason}

Mensaje:
${formData.message}
    `;

      const mailtoUrl = enUsa
        ? `mailto:${
            client?.contact?.email
          }?subject=New contact from ${client?.name}&body=${encodeURIComponent(
            emailBody
          )}`
        : esBrasil
        ? `mailto:${
            client?.contact?.email
          }?subject=Novo contato de ${client?.name}&body=${encodeURIComponent(
            emailBody
          )}`
        : `mailto:${
        client?.contact?.email
      }?subject=Nuevo contacto desde ${client?.name}&body=${encodeURIComponent(
        emailBody
      )}`;

      // Abrir el cliente de correo predeterminado
      window.location.href = mailtoUrl;
    } catch (error) {
      console.error('Error al procesar el formulario:', error);
      alert(`❌ ${t('formsUi.errors.generic')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value: string, id: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Función auxiliar para mapear el motivo a los tipos permitidos
  const mapReasonToType = (reason: string): Mail['reason'] => {
    switch (reason) {
      case 'Compra de Vehículo':
        return 'buy';
      case 'Venta de Vehículo':
        return 'sell';
      default:
        return 'other';
    }
  };

  return (
    <div data-form-section="contact">
      {!embedded && title && (
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <h1 className={titleClass} style={titleStyle}>{title}</h1>
          {subtitle && <p className={subtitleClass} style={subtitleStyle}>{subtitle}</p>}
        </div>
      )}
      <div className={embedded ? '' : (hasBuilderStyles ? 'max-w-2xl mx-auto' : 'grid grid-cols-1 md:grid-cols-2 gap-16')}>
        {/* Contact Form */}
        <div className={embedded ? '' : cardClass} style={embedded ? undefined : cardStyle}>
          <form onSubmit={handleSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <Input
                type='text'
                label={t('formsUi.contact.firstName')}
                autoComplete='given-name'
                value={formData.first_name}
                onValueChange={(value) => handleChange(value, 'first_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
              <Input
                type='text'
                label={t('formsUi.contact.lastName')}
                autoComplete='family-name'
                value={formData.last_name}
                onValueChange={(value) => handleChange(value, 'last_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
            </div>

            <Input
              type='email'
              label={t('formsUi.contact.email')}
              autoComplete='email'
              value={formData.email}
              onValueChange={(value) => handleChange(value, 'email')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Input
              type='tel'
              label={t('formsUi.contact.phone')}
              autoComplete='tel'
              value={formData.phone}
              onValueChange={(value) => handleChange(value, 'phone')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Select
              label={t('formsUi.contact.reason')}
              selectedKeys={formData.reason ? [formData.reason] : []}
              onChange={(e) => handleChange(e.target.value, 'reason')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            >
              <SelectItem key='Compra de Vehículo' value='Compra de Vehículo'>
                {t('formsUi.contact.reasons.buy')}
              </SelectItem>
              <SelectItem key='Venta de Vehículo' value='Venta de Vehículo'>
                {t('formsUi.contact.reasons.sell')}
              </SelectItem>
              <SelectItem key='Consulta General' value='Consulta General'>
                {t('formsUi.contact.reasons.general')}
              </SelectItem>
              <SelectItem key='Otro' value='Otro'>
                {t('formsUi.contact.reasons.other')}
              </SelectItem>
            </Select>

            <Textarea
              label={t('formsUi.contact.message')}
              value={formData.message}
              onValueChange={(value) => handleChange(value, 'message')}
              minRows={4}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Button
              type='submit'
              color='primary'
              fullWidth
              className={'h-12 sm:h-10 ' + (embedded ? 'font-semibold !text-white hover:opacity-90' : 'font-semibold bg-primary text-secondary hover:bg-primary/90 dark:bg-primary dark:text-secondary dark:hover:bg-primary/90')}
              style={buttonStyle}
              isLoading={loading}
            >
              {t('formsUi.contact.submit')}
            </Button>
          </form>
        </div>

        {/* Contact Information */}
        {!embedded && !hasBuilderStyles && (
        <div className={infoCardClass} style={infoCardStyle}>
          <div className='space-y-8'>
            <h2 className={headingClass} style={headingStyle}>
              {t('formsUi.contact.infoTitle')}
            </h2>

            {contactAddress && (
              <div>
                <h3 className={subHeadingClass} style={headingStyle}>
                  {t('formsUi.contact.location')}
                </h3>
                <p className={`mt-2 ${bodyClass}`} style={bodyStyle}>
                  {contactAddress}
                </p>
              </div>
            )}

            {hours.length > 0 && (
              <div>
                <h3 className={subHeadingClass} style={headingStyle}>
                  {t('formsUi.contact.hours')}
                </h3>
                <div className={`mt-2 ${bodyClass}`} style={bodyStyle}>
                  <OpeningHours hours={hours} />
                </div>
              </div>
            )}

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('formsUi.contact.directContact')}
              </h3>
              <p className={`mt-2 ${bodyClass}`} style={bodyStyle}>
                {contactEmails.length > 0 && (
                  <>
                    {contactEmails.length > 1
                      ? t('formsUi.contact.emailsLabel')
                      : t('formsUi.contact.emailLabel')}
                    :{' '}
                    {contactEmails.map((e, i) => (
                      <span key={i}>
                        {i > 0 && <br />}
                        {e}
                      </span>
                    ))}
                    <br />
                  </>
                )}
                {`${t('formsUi.contact.phoneLabel')}: `}{contactPhone}
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        leadType={LeadTypes.CONTACT_GENERAL}
      />
    </div>
  );
};

export default ContactForm;
